import { Fragment, useEffect, useMemo, useRef } from 'react';
import {
  Alert,
  DialogContent,
  DialogActions,
  Button,
  Box,
  LinearProgress,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Stack,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { DialogHeader } from '@/components/common/DialogHeader';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PrintIcon from '@mui/icons-material/Print';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import type { PurchaseOrder, PurchaseOrderStatus } from '@/types';
import { dateFnsLocale } from '@/utils/locale';
import { queryKeys } from '@/hooks/queryKeys';
import { useSupplierDispatchSettings, useDispatchAccess } from '@/hooks/useProcurementDispatch';
import { supplierInvoiceKeys, useInvoiceFacts } from '@/hooks/useSupplierInvoices';
import { PurchaseOrderDispatchPanel } from '@/features/purchase-orders/dispatch';
import { PurchaseOrderReceivingPanel, type ReceivingLine } from '@/features/purchase-orders/receiving';
import { useReceivingAccess } from '@/features/purchase-orders/receiving/receivingSession';
import { SupplierInvoicesPanel, useInvoiceAccess } from '@/features/purchase-orders/invoices';

/** Whether the order was read from the server, as opposed to only the list row it was opened from. */
export type PurchaseOrderDetailState = 'loading' | 'ready' | 'error';

interface PurchaseOrderDetailModalProps {
  open: boolean;
  onClose: () => void;
  purchaseOrder: PurchaseOrder | null;
  /**
   * `ready` only when `purchaseOrder` is the server's own, current order (lines, counters and `updatedAt`). Until then the
   * receiving, sending and invoice sections are not mounted: they need real line ids and a real revision.
   */
  detailState?: PurchaseOrderDetailState;
  onRetryDetail?: () => void;
  /** The workshop's parts ordering module. Off: nothing of receiving, sending and supplier invoices is offered. */
  procurementEnabled?: boolean;
  onEdit?: (po: PurchaseOrder) => void;
  onApprove?: (po: PurchaseOrder) => void;
  /** The manual `approved -> ordered` transition. The server refuses it while a sending is open. */
  onMarkOrdered?: (po: PurchaseOrder) => void;
  onPrint?: (po: PurchaseOrder) => void;
  /** The older single-invoice match. Offered only where the order is not owned by the per-line flow. */
  onMatchInvoice?: (po: PurchaseOrder) => void;
}

const RECEIVING_STATUSES: readonly PurchaseOrderStatus[] = ['ordered', 'partially_received', 'received'];
const INVOICE_STATUSES: readonly PurchaseOrderStatus[] = ['partially_received', 'received'];

type LegacyMatch = 'none' | 'offer' | 'checking' | 'unknown' | 'lineOwned' | 'headerOwned';

/** The receiving screens' line shape. A counter the order does not state stays unstated: it is never turned into zero. */
function receivingLinesOf(po: PurchaseOrder): ReceivingLine[] | null {
  const lines: ReceivingLine[] = [];
  for (const item of po.items) {
    if (item.receivedQuantity === undefined) return null;
    lines.push({
      id: item.id,
      partNumber: item.partNumber,
      name: item.name,
      quantity: item.quantity,
      quantityReceived: item.receivedQuantity,
      quantityHeld: item.heldQuantity,
      quantityCancelled: item.cancelledQuantity,
    });
  }
  return lines;
}

const statusColors: Record<PurchaseOrderStatus, 'default' | 'warning' | 'info' | 'primary' | 'success' | 'error'> = {
  draft: 'default',
  approved: 'info',
  ordered: 'primary',
  partially_received: 'warning',
  received: 'success',
  cancelled: 'error',
};

export function PurchaseOrderDetailModal({
  open,
  onClose,
  purchaseOrder: po,
  detailState = 'ready',
  onRetryDetail,
  procurementEnabled = true,
  onEdit,
  onApprove,
  onMarkOrdered,
  onPrint,
  onMatchInvoice,
}: PurchaseOrderDetailModalProps) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const queryClient = useQueryClient();
  const dispatchAccess = useDispatchAccess();
  const receivingAccess = useReceivingAccess();
  const invoiceAccess = useInvoiceAccess();

  // Which of the procurement sections are on offer. Every input is reactive: a lost right or a module switched off removes the
  // section in the same render. The sections still enforce the owner, rights and order boundary themselves, and the server decides.
  const procurement = open && procurementEnabled && detailState === 'ready' && po !== null;
  const status = po?.status ?? null;
  const receivingLines = useMemo(() => (po ? receivingLinesOf(po) : null), [po]);
  const showDispatch =
    procurement && status !== 'draft' && (dispatchAccess.canReadStatus || dispatchAccess.canDispatch);
  const showReceiving =
    procurement &&
    status !== null &&
    RECEIVING_STATUSES.includes(status) &&
    receivingLines !== null &&
    (receivingAccess.canRead || receivingAccess.canReceive);
  const showInvoices = procurement && status !== null && INVOICE_STATUSES.includes(status) && invoiceAccess.canRead;

  // The supplier's sending mode is only a hint for the review, and only when the server stated it and says the channel works.
  const supplierSettings = useSupplierDispatchSettings(showDispatch && po ? po.supplierId : null);
  const settings = supplierSettings.data;
  const defaultChannel =
    po && settings && settings.supplierId === po.supplierId && settings.mode !== 'manual' && settings.capability[settings.mode].available
      ? settings.mode
      : undefined;

  // How the order is matched to invoices comes from the invoice facts of the server, never from the client's own evaluation.
  const wantsFacts = procurement && status === 'received' && invoiceAccess.canRead;
  const facts = useInvoiceFacts(wantsFacts && po ? po.id : '');

  // A confirmed receipt, hold decision or close-short changes what the invoice screens read. The order refetches (the receiving
  // writes invalidate it), so a changed revision or counter here is the moment the invoice facts are read again.
  const countersSignature = po
    ? JSON.stringify([po.updatedAt, po.items.map((item) => [item.id, item.receivedQuantity, item.heldQuantity, item.rejectedQuantity, item.cancelledQuantity])])
    : '';
  const lastSignature = useRef<{ id: string | null; signature: string }>({ id: null, signature: '' });
  const poId = po?.id ?? null;
  const invoiceOwner = invoiceAccess.owner;
  useEffect(() => {
    const previous = lastSignature.current;
    lastSignature.current = { id: poId, signature: countersSignature };
    if (!showInvoices || invoiceOwner === null || poId === null) return;
    if (previous.id === poId && previous.signature !== countersSignature) {
      void queryClient.invalidateQueries({ queryKey: supplierInvoiceKeys.owner(invoiceOwner) });
    }
  }, [countersSignature, poId, showInvoices, invoiceOwner, queryClient]);

  // The older single-invoice match is for an order that the per-line flow does not own. Where that is not known it is not offered.
  const legacyMatch: LegacyMatch = (() => {
    if (!po || !procurementEnabled || detailState !== 'ready' || po.status !== 'received' || po.invoiceMatched) return 'none';
    if (po.invoiceMatchLevel === 'line') return 'lineOwned';
    if (po.invoiceMatchLevel === 'header') return 'headerOwned';
    if (!invoiceAccess.canRead) return 'offer';
    if (!facts.data) return facts.isError ? 'unknown' : 'checking';
    const { purchaseOrder: ownership, documents } = facts.data;
    if (ownership.invoiceMatchLevel === 'line' || documents.length > 0) return 'lineOwned';
    if (ownership.invoiceMatchLevel === 'header') return 'headerOwned';
    return invoiceAccess.canMatch ? 'none' : 'offer';
  })();

  const statusLabels: Record<PurchaseOrderStatus, string> = {
    draft: t('purchaseOrders.status.draft'),
    approved: t('purchaseOrders.status.approved'),
    ordered: t('purchaseOrders.status.ordered'),
    partially_received: t('purchaseOrders.status.partiallyReceived'),
    received: t('purchaseOrders.status.received'),
    cancelled: t('purchaseOrders.status.cancelled'),
  };

  if (!po) return null;

  const showReceivedColumn = po.status === 'received' || po.status === 'partially_received';
  // Held, rejected and cancelled units are shown only for an order that states them; a line without the figure shows a dash.
  const showCounterColumns =
    showReceivedColumn &&
    po.items.some((item) => item.heldQuantity !== undefined || item.rejectedQuantity !== undefined || item.cancelledQuantity !== undefined);
  const counterCell = (value: number | undefined) =>
    value === undefined ? (
      <Typography variant="body2" color="text.secondary" title={t('purchaseOrderDetail.counters.unknownHint')}>
        {t('purchaseOrderDetail.counters.unknown')}
      </Typography>
    ) : (
      <Typography variant="body2">{`${value} ${t('purchaseOrderDetail.pcs')}`}</Typography>
    );

  return (
    <ResponsiveDialog mobileTitle={t('purchaseOrderDetail.purchaseOrder')} open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogHeader onClose={onClose}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">{t('purchaseOrderDetail.purchaseOrder')} {po.orderNumber}</Typography>
            <Chip label={statusLabels[po.status]} color={statusColors[po.status]} size="small" />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {po.supplier.name}
          </Typography>
        </Box>
      </DialogHeader>

      <DialogContent dividers>
        {/* Info Grid */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('purchaseOrderDetail.supplier')}
              </Typography>
              <Typography variant="body1" fontWeight={600} gutterBottom>
                {po.supplier.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('purchaseOrderDetail.deliveryTime', { days: po.supplier.deliveryDays })}
              </Typography>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('purchaseOrderDetail.dates')}
              </Typography>
              <Stack spacing={0.5}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">{t('purchaseOrderDetail.created')}:</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {format(new Date(po.createdAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() })}
                  </Typography>
                </Box>
                {po.approvedAt && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{t('purchaseOrderDetail.approved')}:</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {format(new Date(po.approvedAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() })}
                    </Typography>
                  </Box>
                )}
                {po.orderedAt && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{t('purchaseOrderDetail.ordered')}:</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {format(new Date(po.orderedAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() })}
                    </Typography>
                  </Box>
                )}
                {po.expectedDeliveryDate && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{t('purchaseOrderDetail.expectedDelivery')}:</Typography>
                    <Typography variant="body2" fontWeight={500} color="primary">
                      {format(new Date(po.expectedDeliveryDate), 'd.M.yyyy', { locale: dateFnsLocale() })}
                    </Typography>
                  </Box>
                )}
                {po.receivedAt && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{t('purchaseOrderDetail.received')}:</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {format(new Date(po.receivedAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() })}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('purchaseOrderDetail.creator')}
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {po.createdByName}
              </Typography>
            </Paper>
          </Grid>

          {po.approvedByName && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('purchaseOrderDetail.approver')}
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {po.approvedByName}
                </Typography>
              </Paper>
            </Grid>
          )}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Items Table */}
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          {t('purchaseOrderDetail.orderedParts')}
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('purchaseOrderForm.colPartNumber')}</TableCell>
                <TableCell>{t('purchaseOrderForm.colName')}</TableCell>
                <TableCell>{t('purchaseOrderDetail.destinationCol')}</TableCell>
                <TableCell align="right">{t('purchaseOrderForm.colQuantity')}</TableCell>
                <TableCell align="right">{t('purchaseOrderForm.colUnitPrice')}</TableCell>
                <TableCell align="right">{t('purchaseOrderForm.colTotal')}</TableCell>
                {showReceivedColumn && <TableCell align="right">{t('purchaseOrderDetail.received')}</TableCell>}
                {showCounterColumns && <TableCell align="right">{t('purchaseOrderDetail.counters.heldCol')}</TableCell>}
                {showCounterColumns && <TableCell align="right">{t('purchaseOrderDetail.counters.rejectedCol')}</TableCell>}
                {showCounterColumns && <TableCell align="right">{t('purchaseOrderDetail.counters.cancelledCol')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {po.items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace" fontWeight={500}>
                      {item.partNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>
                    {item.linkedBookingId ? (
                      <>
                        <Chip
                          size="small"
                          color="warning"
                          label={t('purchaseOrderDetail.destinationTransit')}
                        />
                        {item.linkedWorkOrder && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {t('purchaseOrderDetail.linkedWorkOrder')}:{' '}
                            {[item.linkedWorkOrder.vehicleRegistration, item.linkedWorkOrder.customerName]
                              .filter(Boolean)
                              .join(' · ')}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Chip size="small" variant="outlined" label={t('purchaseOrderDetail.destinationStock')} />
                    )}
                  </TableCell>
                  <TableCell align="right">{item.quantity} {t('purchaseOrderDetail.pcs')}</TableCell>
                  <TableCell align="right">€{(item.unitCostCents / 100).toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>
                      €{(item.totalCostCents / 100).toFixed(2)}
                    </Typography>
                  </TableCell>
                  {showReceivedColumn && (
                    <TableCell align="right">
                      {item.receivedQuantity === undefined ? (
                        counterCell(undefined)
                      ) : (
                        <Chip
                          label={`${item.receivedQuantity} ${t('purchaseOrderDetail.pcs')}`}
                          size="small"
                          color={item.receivedQuantity === item.quantity ? 'success' : 'warning'}
                        />
                      )}
                    </TableCell>
                  )}
                  {showCounterColumns && <TableCell align="right">{counterCell(item.heldQuantity)}</TableCell>}
                  {showCounterColumns && <TableCell align="right">{counterCell(item.rejectedQuantity)}</TableCell>}
                  {showCounterColumns && <TableCell align="right">{counterCell(item.cancelledQuantity)}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 3 }} />

        {/* Cost Summary */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ minWidth: 300 }}>
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">{t('purchaseOrderForm.subtotal')}</Typography>
                <Typography variant="body2">€{(po.subtotalCents / 100).toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">{t('purchaseOrderForm.vat', {
                  rate:
                    po.subtotalCents > 0
                      ? String(Math.round((po.taxCents / po.subtotalCents) * 1000) / 10).replace('.', ',')
                      : '25,5',
                })}</Typography>
                <Typography variant="body2">€{(po.taxCents / 100).toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">{t('purchaseOrderForm.shipping')}</Typography>
                <Typography variant="body2">€{(po.shippingCents / 100).toFixed(2)}</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6">{t('purchaseOrderForm.total')}</Typography>
                <Typography variant="h6" color="primary">
                  €{(po.totalCents / 100).toFixed(2)}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Box>

        {/* Invoice matching: the older single-invoice match and the per-line flow are told apart, never mixed up. */}
        {detailState === 'ready' && (po.invoiceMatched || po.status === 'received') && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {t('purchaseOrderDetail.invoiceMatch.title')}
                </Typography>
                {po.invoiceMatched && po.supplierInvoiceNumber ? (
                  <Stack spacing={0.5} data-testid="legacy-header-match">
                    <Typography variant="caption" color="text.secondary">
                      {t('purchaseOrderDetail.invoiceMatch.headerTitle')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('purchaseOrderDetail.invoiceMatch.numberLabel')}: <strong>{po.supplierInvoiceNumber}</strong>
                      </Typography>
                      {po.supplierInvoiceDate && (
                        <Typography variant="body2" color="text.secondary">
                          {t('purchaseOrderDetail.invoiceMatch.dateLabel')}:{' '}
                          <strong>{format(new Date(po.supplierInvoiceDate), 'd.M.yyyy', { locale: dateFnsLocale() })}</strong>
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {t('purchaseOrderDetail.invoiceMatch.totalLabel')}:{' '}
                        <strong>
                          {po.supplierInvoiceTotalCents == null
                            ? t('purchaseOrderDetail.invoiceMatch.totalUnknown')
                            : `€${(po.supplierInvoiceTotalCents / 100).toFixed(2)}`}
                        </strong>
                      </Typography>
                    </Box>
                    <Box>
                      {po.invoiceDiscrepancyCents == null ? null : po.invoiceDiscrepancyCents === 0 ? (
                        <Chip
                          size="small"
                          color="success"
                          label={t('purchaseOrderDetail.invoiceMatch.exactMatch')}
                        />
                      ) : (
                        <Chip
                          size="small"
                          color="warning"
                          label={t('purchaseOrderDetail.invoiceMatch.discrepancy', {
                            amount: (po.invoiceDiscrepancyCents / 100).toFixed(2),
                          })}
                        />
                      )}
                    </Box>
                    {po.invoiceMatchedByName && po.invoiceMatchedAt && (
                      <Typography variant="caption" color="text.secondary">
                        {t('purchaseOrderDetail.invoiceMatch.matchedBy', {
                          name: po.invoiceMatchedByName,
                          date: format(new Date(po.invoiceMatchedAt), 'd.M.yyyy HH:mm', { locale: dateFnsLocale() }),
                        })}
                      </Typography>
                    )}
                  </Stack>
                ) : po.invoiceMatched ? (
                  // Matched by the per-line flow: there is no single invoice to name, so none is shown.
                  <Typography variant="body2" color="text.secondary" data-testid="line-match-summary">
                    {t('purchaseOrderDetail.invoiceMatch.lineMatched')}
                  </Typography>
                ) : (
                  <Stack spacing={0.5}>
                    <Typography variant="body2" color="text.secondary">
                      {t('purchaseOrderDetail.invoiceMatch.notMatched')}
                    </Typography>
                    {legacyMatch === 'lineOwned' && (
                      <Typography variant="body2" color="text.secondary" data-testid="legacy-match-reason">
                        {t('purchaseOrderDetail.invoiceMatch.lineOwned')}
                      </Typography>
                    )}
                    {legacyMatch === 'headerOwned' && (
                      <Typography variant="body2" color="text.secondary" data-testid="legacy-match-reason">
                        {t('purchaseOrderDetail.invoiceMatch.headerOwned')}
                      </Typography>
                    )}
                    {legacyMatch === 'checking' && (
                      <Typography variant="body2" color="text.secondary" role="status">
                        {t('purchaseOrderDetail.invoiceMatch.checking')}
                      </Typography>
                    )}
                    {legacyMatch === 'unknown' && (
                      <Typography variant="body2" color="text.secondary" data-testid="legacy-match-reason">
                        {t('purchaseOrderDetail.invoiceMatch.checkFailed')}
                      </Typography>
                    )}
                  </Stack>
                )}
              </Box>
              {legacyMatch === 'offer' && onMatchInvoice && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ReceiptLongIcon />}
                  onClick={() => { onMatchInvoice(po); }}
                >
                  {t('purchaseOrderDetail.invoiceMatch.matchButton')}
                </Button>
              )}
            </Box>
          </>
        )}

        {/* Notes */}
        {po.notes && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="subtitle2" gutterBottom>
              {t('purchaseOrderDetail.notes')}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
              <Typography variant="body2">{po.notes}</Typography>
            </Paper>
          </>
        )}

        {/* The order as the server has it. Sending, receiving and invoices need its real lines and revision, so wait for it. */}
        {open && procurementEnabled && detailState === 'loading' && (
          <Box sx={{ mt: 3 }} role="status" aria-busy="true">
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('purchaseOrderDetail.detailLoading')}
            </Typography>
          </Box>
        )}
        {open && procurementEnabled && detailState === 'error' && (
          <Alert
            severity="warning"
            sx={{ mt: 3 }}
            action={
              onRetryDetail ? (
                <Button color="inherit" size="small" onClick={onRetryDetail}>
                  {t('purchaseOrderDetail.retryDetail')}
                </Button>
              ) : undefined
            }
          >
            {t('purchaseOrderDetail.detailUnavailable')}
          </Alert>
        )}

        {/* Keyed by the order: another order starts with none of this one's drafts, reviews or dialogs. */}
        <Fragment key={po.id}>
          {showDispatch && (
            <>
              <Divider sx={{ my: 3 }} />
              <PurchaseOrderDispatchPanel
                purchaseOrderId={po.id}
                documentRevision={po.updatedAt}
                {...(defaultChannel !== undefined ? { defaultChannel } : {})}
                onPurchaseOrderChanged={() => {
                  // A queued, cancelled or reconciled sending can move the order itself (a sent order is ordered on the server).
                  void queryClient.invalidateQueries({ queryKey: queryKeys.inventory.purchaseOrders.all });
                }}
              />
            </>
          )}
          {showReceiving && receivingLines !== null && (
            <>
              <Divider sx={{ my: 3 }} />
              <PurchaseOrderReceivingPanel purchaseOrderId={po.id} orderNumber={po.orderNumber} lines={receivingLines} />
            </>
          )}
          {showInvoices && (
            <>
              <Divider sx={{ my: 3 }} />
              <SupplierInvoicesPanel purchaseOrderId={po.id} />
            </>
          )}
        </Fragment>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
          <Box>
            {onPrint && (
              <Button startIcon={<PrintIcon />} onClick={() => onPrint(po)}>
                {t('purchaseOrderDetail.print')}
              </Button>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button onClick={onClose}>{tc('close')}</Button>
            {po.status === 'draft' && onEdit && (
              <Button variant="outlined" startIcon={<EditIcon />} onClick={() => onEdit(po)}>
                {tc('edit')}
              </Button>
            )}
            {po.status === 'draft' && onApprove && (
              <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={() => onApprove(po)}>
                {t('purchaseOrders.approve')}
              </Button>
            )}
            {po.status === 'approved' && onMarkOrdered && (
              <Button variant="contained" startIcon={<LocalShippingIcon />} onClick={() => onMarkOrdered(po)}>
                {t('purchaseOrders.markOrdered')}
              </Button>
            )}
          </Stack>
        </Box>
      </DialogActions>
    </ResponsiveDialog>
  );
}
